/**
 * "DEINE BOX" — Signature 1, the flight.
 *
 * With the drawer no longer opening on every add (candy-quiet-cart.js), the
 * add needs its own confirmation, and the plan's is physical rather than
 * textual: the product itself arcs up out of the page and drops into the
 * box in the header, which squishes on impact.
 *
 * The arc is sampled from a quadratic bezier into explicit keyframes rather
 * than driven by offset-path. Both would draw the same curve, but keyframes
 * let the scale and spin ride the same normalised t as the position, so the
 * thing shrinks *into* the box instead of shrinking beside it, and there is
 * no second coordinate space to keep in sync.
 *
 * The squish is deliberately fired here on landing, not by candy-cart-box.js
 * on cartUpdate: the network round-trip usually finishes long before the
 * flight does, so leaving it on the cart event made the box react to
 * something the customer had not yet seen arrive.
 */
(function () {
  var FLIGHT_MS = 720;
  var SAMPLES = 26;

  function reduceMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function onScreen(el) {
    if (!el || el.hidden) return false;
    var r = el.getBoundingClientRect();
    if (!r.width || !r.height) return false;
    return r.bottom > 0 && r.top < window.innerHeight &&
           r.right > 0 && r.left < window.innerWidth;
  }

  function cartTarget() {
    var float = document.querySelector('[data-cart-float]');
    if (onScreen(float)) return float;

    var candidates = Array.prototype.slice.call(document.querySelectorAll('[data-cart-box]'));
    for (var i = 0; i < candidates.length; i++) {
      if (onScreen(candidates[i])) return candidates[i];
    }
    var bubble = document.querySelector('#cart-icon-bubble');
    if (onScreen(bubble)) return bubble;

    // Nothing on screen at all: fall back to the header's box anyway and let
    // clampToViewport() below keep the landing point inside the window, so
    // the arc still resolves somewhere visible instead of sailing off.
    return candidates[0] || bubble;
  }

  // Last-resort guard: a landing point outside the window means the customer
  // watches the product leave and never arrive.
  function clampToViewport(x, y) {
    var m = 34;
    return {
      x: Math.max(m, Math.min(window.innerWidth - m, x)),
      y: Math.max(m, Math.min(window.innerHeight - m, y))
    };
  }

  // Walk out from the button through the containers an add can live in and
  // take that context's own product image.
  var CONTAINERS = [
    '.candy-bundle-item',
    '.candy-cart-upsell__item',
    '.card-wrapper',
    '.card',
    '.grid__item',
    '.candy-quick-add',
    '.product__info-container',
    '.product',
    'form'
  ];

  function sourceImage(button) {
    for (var i = 0; i < CONTAINERS.length; i++) {
      var box = button.closest(CONTAINERS[i]);
      if (!box) continue;
      // on a product page the info column holds no photo -- reach across to
      // the gallery's currently visible slide instead
      var img = box.querySelector('img');
      if (!img && box.matches('.product__info-container, .product')) {
        img = document.querySelector('.product__media-item:not([hidden]) img, .product__media img');
      }
      if (img && img.currentSrc) return img;
    }
    return document.querySelector('.product__media img');
  }

  function fly(img, button) {
    var target = cartTarget();
    if (!target) return;

    var origin = onScreen(img) ? img : button;
    var from = origin.getBoundingClientRect();
    var to = target.getBoundingClientRect();
    if (!from.width || !to.width) return;

    var sx = from.left + from.width / 2;
    var sy = from.top + from.height / 2;
    var landing = clampToViewport(to.left + to.width / 2, to.top + to.height / 2);
    var ex = landing.x;
    var ey = landing.y;

    var flyer = document.createElement('div');
    flyer.className = 'candy-flyer';
    flyer.setAttribute('aria-hidden', 'true');
    if (img && img.currentSrc) {
      flyer.style.backgroundImage = 'url("' + img.currentSrc + '")';
    } else {
      flyer.classList.add('candy-flyer--plain');
    }
    document.body.appendChild(flyer);

    // Lift scales with how far it has to travel, so a short hop from a cart
    // upsell does not loop as high as a flight from the bottom of a long
    // product page. Clamped so the arc never sails off the top of the view.
    var dist = Math.hypot(ex - sx, ey - sy);
    var cx = (sx + ex) / 2;
    var cy = Math.max(28, Math.min(sy, ey) - Math.max(90, dist * 0.32));

    var frames = [];
    for (var i = 0; i <= SAMPLES; i++) {
      var t = i / SAMPLES;
      var inv = 1 - t;
      var x = inv * inv * sx + 2 * inv * t * cx + t * t * ex;
      var y = inv * inv * sy + 2 * inv * t * cy + t * t * ey;
      frames.push({
        transform: 'translate(' + x + 'px, ' + y + 'px) translate(-50%, -50%)' +
                   ' scale(' + (1 - 0.78 * t).toFixed(3) + ')' +
                   ' rotate(' + (t * 168).toFixed(1) + 'deg)',
        opacity: t < 0.88 ? 1 : Math.max(0, 1 - (t - 0.88) / 0.12)
      });
    }

    var anim = flyer.animate(frames, {
      duration: FLIGHT_MS,
      easing: 'cubic-bezier(.45,.05,.35,1)',
      fill: 'forwards'
    });

    var landed = false;
    function land() {
      if (landed) return;
      landed = true;
      flyer.remove();
      if (window.CandyCartBox && typeof window.CandyCartBox.pulse === 'function') {
        window.CandyCartBox.pulse();
      }
      window.__candyFlightActive = false;
    }
    anim.addEventListener('finish', land);
    // WAAPI never fires finish on a backgrounded tab; without this the
    // flyer would be left pinned over the page on return.
    setTimeout(land, FLIGHT_MS + 260);
  }

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var button = target.closest(
        'button[name="add"], .candy-quick-add-btn, .candy-bundle-add-to-cart-btn, [data-candy-add]'
      );
      if (!button || button.disabled) return;
      if (button.closest('modal-opener')) return;

      if (reduceMotion()) {
        // No flight, but the box still acknowledges the add.
        if (window.CandyCartBox && window.CandyCartBox.pulse) {
          setTimeout(window.CandyCartBox.pulse, 200);
        }
        return;
      }

      window.__candyFlightActive = true;
      var img = sourceImage(button);
      // Read geometry before the click's own handler can re-render anything
      // underneath us.
      fly(img, button);
    },
    true
  );
})();
